import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import ScoringBreakdown from "./scoring-breakdown";

import "./match-pair-card.css";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const formatPenalty = (penalty) => {
  if (!penalty) return "N/A";
  let str = "";
  if (penalty.type === 1) str = `${penalty.value}%`;
  else if (penalty.type === 2) str = `$${penalty.value?.toLocaleString()}`;
  else str = `${penalty.value}`;
  if (penalty.dueDate) {
    str += ` on ${moment(penalty.dueDate).utc().format("M/D/YY")}`;
  }
  return str;
};

export const LegSide = ({ leg, align }) => {
  if (!leg) return <div className="match-pair-card__leg">&mdash;</div>;

  const depCode = leg.departureAirport?.code || "???";
  const arrCode = leg.arrivalAirport?.code || "???";
  const depMuni = leg.departureAirport?.municipality || "";
  const arrMuni = leg.arrivalAirport?.municipality || "";
  const dateStr = leg.departureDateString
    ? moment(leg.departureDateString).utc().format("MMMM Do, YYYY")
    : "—";

  const isRight = align === "right";

  return (
    <div
      className={`match-pair-card__leg ${
        isRight ? "match-pair-card__leg--right" : ""
      }`}
    >
      <div className="match-pair-card__date">{dateStr}</div>
      <div className="match-pair-card__route">
        {depCode}
        <span className="match-pair-card__route-arrow">&rarr;</span>
        {arrCode}
      </div>
      <div className="match-pair-card__municipalities">
        {depMuni} to {arrMuni}
      </div>
      <div
        className={`match-pair-card__owner-badge ${
          !leg.assigneeName
            ? "match-pair-card__owner-badge--unassigned"
            : leg._isMine
            ? "match-pair-card__owner-badge--mine"
            : "match-pair-card__owner-badge--other"
        }`}
      >
        {leg.assigneeName || "Unassigned"}
      </div>
    </div>
  );
};

export const LegDetails = ({ leg, align }) => {
  if (!leg) return null;

  const isRight = align === "right";

  const recordTypeLabel =
    leg.sourceType === "quote"
      ? "Quote Details"
      : leg.sourceType === "contract"
      ? "Contract Details"
      : leg.sourceType === "lead"
      ? "Lead Details"
      : "Record Details";

  const viewRecordLabel =
    leg.sourceType === "quote"
      ? "View Quote"
      : leg.sourceType === "contract"
      ? leg.tripId
        ? "View Trip"
        : "View Contract"
      : leg.sourceType === "lead"
      ? "View Lead"
      : "View Record";

  const recordSlug =
    leg.sourceType === "quote"
      ? "quotes"
      : leg.sourceType === "contract"
      ? leg.tripId
        ? "trips"
        : "contracts"
      : leg.sourceType === "lead"
      ? "leads"
      : null;

  return (
    <div className={`match-pair-card__details ${isRight ? "match-pair-card__details--right" : ""}`}>
      {/* Assignee */}
      <div className="match-pair-card__detail-section">
        <div className="match-pair-card__detail-label">Assignee</div>
        <div
          className={`match-pair-card__owner-badge ${
            !leg.assigneeName
              ? "match-pair-card__owner-badge--unassigned"
              : leg._isMine
              ? "match-pair-card__owner-badge--mine"
              : "match-pair-card__owner-badge--other"
          }`}
        >
          {leg.assigneeName || "Unassigned"}
        </div>
      </div>

      {/* Leg Origin */}
      <div className="match-pair-card__detail-section">
        <div className="match-pair-card__detail-label">Leg Origin</div>
        <div className="match-pair-card__detail-value">
          {leg.sourceLabel}
          {leg.prospectName ? ` | ${leg.prospectName}` : ""}
        </div>
        <div className="match-pair-card__links">
          {recordSlug && (leg.sourceType === "contract" && leg.tripId ? leg.tripId : leg.sourceRecordId) && (
            <Link
              className="match-pair-card__link"
              to={`/${recordSlug}/${leg.sourceType === "contract" && leg.tripId ? leg.tripId : leg.sourceRecordId}`}
            >
              {viewRecordLabel}
            </Link>
          )}
          {leg.leadId && leg.sourceType !== "lead" && (
            <Link className="match-pair-card__link" to={`/leads/${leg.leadId}`}>
              View Lead
            </Link>
          )}
          {leg.tripId && leg.sourceType !== "contract" && (
            <Link className="match-pair-card__link" to={`/trips/${leg.tripId}`}>
              View Trip
            </Link>
          )}
          {leg.evoProspectId && (
            <Link className="match-pair-card__link" to={`/prospects/${leg.evoProspectId}`}>
              View Prospect
            </Link>
          )}
        </div>
      </div>

      {/* Source Record Legs */}
      {leg.sourceRecordLegs?.length > 0 && (
        <div className="match-pair-card__detail-section">
          <div className="match-pair-card__detail-label">{recordTypeLabel}</div>
          <ul className="match-pair-card__record-legs">
            {leg.sourceRecordLegs.map((rl, i) => {
              const isThisLeg =
                rl.depCode === leg.departureAirport?.code &&
                rl.arrCode === leg.arrivalAirport?.code;
              const dateStr = rl.departureDate
                ? moment(rl.departureDate).utc().format("M/D/YY")
                : "";
              return (
                <li
                  key={i}
                  className={
                    isThisLeg ? "match-pair-card__record-leg--highlighted" : ""
                  }
                >
                  Leg {i + 1}: {dateStr ? `${dateStr} ` : ""}
                  {rl.depCode}-{rl.arrCode}
                </li>
              );
            })}
          </ul>
          {leg.sourceRecordAircraft?.length > 0 && (
            <div className="match-pair-card__aircraft-list">
              {leg.sourceRecordAircraft.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* OFR Data (contracts only) */}
      {leg.ofrData && (
        <div className="match-pair-card__detail-section">
          <div className="match-pair-card__detail-label">Signed OFRs</div>
          <div className="match-pair-card__ofr-value">
            {leg.ofrData.ofrTripId && leg.ofrData.ofrId ? (
              <Link className="match-pair-card__link" to={`/trips/${leg.ofrData.ofrTripId}/financials/${leg.ofrData.ofrId}`}>
                {leg.ofrData.operatorName || "\u2014"}
              </Link>
            ) : (
              leg.ofrData.operatorName || "\u2014"
            )}
          </div>
          <div className="match-pair-card__ofr-value">
            Current Penalty - {formatPenalty(leg.ofrData.currentPenalty)}
          </div>
          <div className="match-pair-card__ofr-value">
            Next Penalty - {formatPenalty(leg.ofrData.nextPenalty)}
          </div>
        </div>
      )}

      {/* ATC Details (atc legs only) */}
      {leg.atcDetails && (
        <div className="match-pair-card__detail-section">
          <div className="match-pair-card__detail-label">ATC Details</div>
          {leg.atcDetails.createdAt && (
            <div className="match-pair-card__ofr-value">
              Created: {moment(leg.atcDetails.createdAt).utc().format("M/D/YY")}
            </div>
          )}
          {leg.atcDetails.passengers != null && (
            <div className="match-pair-card__ofr-value">
              Passengers: {leg.atcDetails.passengers}
            </div>
          )}
          {leg.atcDetails.priorityLabel && (
            <div className="match-pair-card__ofr-value">
              Priority: {leg.atcDetails.priorityLabel}
            </div>
          )}
          {leg.atcDetails.aircraft?.length > 0 && (
            <div className="match-pair-card__ofr-value">
              Aircraft: {leg.atcDetails.aircraft.join(", ")}
            </div>
          )}
          {leg.atcDetails.notes && (
            <div className="match-pair-card__ofr-value">
              Notes: {leg.atcDetails.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MatchPairCard = ({
  pair,
  currentUserId,
  index = 0,
  isSuperAdmin,
  onArchived,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archived, setArchived] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  if (!pair) return null;

  // Placement logic: current user's leg goes left
  let legA = pair.legA;
  let legB = pair.legB;

  if (legA && legB) {
    const aIsMine = legA.assigneeId === currentUserId;
    const bIsMine = legB.assigneeId === currentUserId;

    if (bIsMine && !aIsMine) {
      [legA, legB] = [legB, legA];
    } else if (!aIsMine && !bIsMine) {
      const dateA = new Date(legA.departureDateString || 0);
      const dateB = new Date(legB.departureDateString || 0);
      if (dateB < dateA) {
        [legA, legB] = [legB, legA];
      } else if (dateA.getTime() === dateB.getTime()) {
        if ((legB._id || "") < (legA._id || "")) {
          [legA, legB] = [legB, legA];
        }
      }
    }

    legA = { ...legA, _isMine: legA.assigneeId === currentUserId };
    legB = { ...legB, _isMine: legB.assigneeId === currentUserId };
  }

  const isHighPriority = pair.priority <= 10;
  const hasCore = pair.corePriority != null;

  const openArchiveModal = (e) => {
    e.stopPropagation();
    setShowArchiveModal(true);
  };

  const closeArchiveModal = () => {
    if (archiving) return;
    setShowArchiveModal(false);
  };

  const confirmArchive = async () => {
    const legAId = pair.legAId || legA?._id;
    const legBId = pair.legBId || legB?._id;
    if (!legAId || !legBId) return;

    setArchiving(true);
    try {
      const token = await getAccessTokenSilently().catch((err) => {
        console.error(err);
        return loginWithRedirect();
      });
      await ai
        .auth(token)
        .patch(`/api/flightLegs/${legAId}/archiveMatch/${legBId}`);

      toast.success("Match archived successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setShowArchiveModal(false);
      setArchived(true);
      if (onArchived) onArchived(pair);
    } catch (err) {
      console.error("Archive match failed:", err);
      toast.error(
        getApiErrorMessage(err, "Failed to archive match. Please try again."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
      setArchiving(false);
    }
  };

  if (archived) return null;

  return (
    <div
      className="match-pair-card"
      style={{ animationDelay: `${Math.min(index, 10) * 100}ms` }}
    >
      <div
        className={`match-pair-card__top-row ${
          isHighPriority
            ? "match-pair-card__top-row--hot"
            : "match-pair-card__top-row--normal"
        }`}
        role="group"
        aria-label="Match scores"
      >
        <div className="match-pair-card__metric">
          <span className="match-pair-card__metric-label">Total Score</span>
          <span className="match-pair-card__metric-value">{pair.priority}</span>
        </div>

        {hasCore && (
          <div
            className="match-pair-card__metric"
            title={`Core Factors score: ${pair.corePriority} of 9`}
          >
            <span className="match-pair-card__metric-label">Core</span>
            <span className="match-pair-card__metric-value">
              {pair.corePriority}
              <span className="match-pair-card__metric-denom">/9</span>
            </span>
          </div>
        )}
      </div>

      <div
        className="match-pair-card__collapsed"
        onClick={() => setExpanded(!expanded)}
      >
        <LegSide leg={legA} align="left" />

        <div className="match-pair-card__separator">
          <div className="match-pair-card__match-icon">&#8596;</div>
          <div className="match-pair-card__match-type">{pair.matchType}</div>
        </div>

        <LegSide leg={legB} align="right" />
      </div>

      <div
        className="match-pair-card__toggle-row"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="match-pair-card__toggle-text">
          {expanded ? "Show less" : "Show more"}
        </span>
      </div>

      <div className={`match-pair-card__expand-wrapper ${expanded ? "match-pair-card__expand-wrapper--open" : ""}`}>
        <div className="match-pair-card__expand-inner">
          <div className="match-pair-card__expanded">
            <div className="match-pair-card__expanded-grid">
              <LegDetails leg={legA} align="left" />
              <LegDetails leg={legB} align="right" />
            </div>
            <div className="match-pair-card__actions">
              <button
                className="match-pair-card__archive-btn"
                onClick={openArchiveModal}
                disabled={archiving}
              >
                Archive Match
              </button>
            </div>
            {isSuperAdmin && (
              <>
                <div className="scoring-breakdown__toggle-row">
                  <button
                    className="scoring-breakdown__toggle-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBreakdown(!showBreakdown);
                    }}
                  >
                    {showBreakdown ? "Hide Score Breakdown" : "View Score Breakdown"}
                  </button>
                </div>
                <ScoringBreakdown
                  legAId={pair.legAId || legA?._id}
                  legBId={pair.legBId || legB?._id}
                  visible={showBreakdown}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ReactModal
        isOpen={showArchiveModal}
        onRequestClose={closeArchiveModal}
        shouldCloseOnEsc={!archiving}
        shouldCloseOnOverlayClick={!archiving}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "600px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Archive Match</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Archiving this match will prevent these two records from ever being
            matched again, even as they progress to quotes or contracts. This
            action can be reversed later.
            <br />
            <br />
            Are you sure you want to continue?
          </div>
        </div>

        <div className="modal-footer w-100 mt-20 d-flex gap-20">
          <button
            className="base-button evo-blue flex-half"
            type="button"
            onClick={closeArchiveModal}
            disabled={archiving}
          >
            Cancel
          </button>

          <LoadingButton
            className="base-button btn-danger flex-half"
            type="button"
            onClick={confirmArchive}
            isLoading={archiving}
          >
            Archive Match
          </LoadingButton>
        </div>
      </ReactModal>
    </div>
  );
};

export default MatchPairCard;
